import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(AuthService);
    const router = inject(Router);

    const isLoginRequest = req.url.includes('/Auth/login');
    const isRefreshRequest = req.url.includes('/Auth/refresh');
    const isRuntimeConfigRequest =
        req.url === '/config/admin-config.json' ||
        req.url.endsWith('/config/admin-config.json') ||
        req.url.includes('/config/admin-config.json');

    if (isLoginRequest || isRefreshRequest || isRuntimeConfigRequest) {
        return next(req);
    }

    const handleLogout = () => {
        authService.clearSession();
        router.navigate(['/login']);
    };

    const sendRequestWithToken = (token: string) => {
        const authReq = req.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`
            }
        });

        return next(authReq).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401) {
                    if (!authService.canRefreshSession()) {
                        handleLogout();
                        return throwError(() => error);
                    }

                    return authService.refreshToken().pipe(
                        switchMap((response) => {
                            const retryReq = req.clone({
                                setHeaders: {
                                    Authorization: `Bearer ${response.token}`
                                }
                            });

                            return next(retryReq);
                        }),
                        catchError((refreshError) => {
                            handleLogout();
                            return throwError(() => refreshError);
                        })
                    );
                }

                return throwError(() => error);
            })
        );
    };

    if (authService.hasUsableSession()) {
        return sendRequestWithToken(authService.getAccessToken());
    }

    if (authService.canRefreshSession()) {
        return authService.refreshToken().pipe(
            switchMap((response) => sendRequestWithToken(response.token)),
            catchError((refreshError) => {
                handleLogout();
                return throwError(() => refreshError);
            })
        );
    }

    handleLogout();
    return throwError(() => new Error('User session is missing or expired.'));
};